import { useEffect, useRef, useState, useCallback } from "react";
import { menu as menuApi } from "../lib/api";

const CATEGORIES = [
  { key: "vodka", label: "Vodka" },
  { key: "tequila", label: "Tequila" },
  { key: "gin", label: "Gin" },
  { key: "cognac", label: "Cognac" },
  { key: "rum", label: "Rum" },
  { key: "scotch", label: "Scotch" },
  { key: "whiskey_bourbon", label: "Whiskey & Bourbon" },
  { key: "champagne", label: "Champagne" },
  { key: "other", label: "Other" },
  { key: "packages", label: "Packages" },
  { key: "ultra_experience", label: "Onyx Ultra" },
];

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(CATEGORIES[0].key);
  const sectionRefs = useRef({});
  const tabsBarRef = useRef(null);

  useEffect(() => { menuApi.list().then(setItems).catch(() => {}); }, []);

  // Track which category is in view to highlight its tab pill
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveCat(visible[0].target.dataset.cat);
      },
      { rootMargin: "-140px 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  const scrollTo = useCallback((key) => {
    const el = sectionRefs.current[key];
    if (!el) return;
    const navOffset = 132; // navbar (≈80) + tabs (≈52)
    const top = el.getBoundingClientRect().top + window.scrollY - navOffset;
    window.scrollTo({ top, behavior: "smooth" });
    // Auto-scroll the tabs row so the active tab is centred on mobile
    setTimeout(() => {
      const bar = tabsBarRef.current;
      const pill = bar?.querySelector(`[data-tab="${key}"]`);
      if (bar && pill) {
        const offset = pill.offsetLeft - (bar.clientWidth - pill.clientWidth) / 2;
        bar.scrollTo({ left: offset, behavior: "smooth" });
      }
    }, 250);
  }, []);

  const visibleCats = CATEGORIES.filter((c) => items.some((i) => i.category === c.key));

  return (
    <main data-testid="menu-page" className="pt-28 min-h-screen pb-24 bg-white">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 pt-12 mb-10">
        <span className="eyebrow">— The Menu</span>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-4 text-ink">Cocktail &amp; <span className="text-gold">Bottle Service Menu</span> — DC's Premier Rooftop</h1>
      </div>

      {/* Sticky category tabs */}
      <div
        data-testid="menu-tabs"
        className="sticky top-20 z-30 bg-white/95 backdrop-blur-md border-y border-black/10 mb-10"
      >
        <div
          ref={tabsBarRef}
          className="max-w-[1500px] mx-auto px-4 md:px-10 lg:px-16 overflow-x-auto no-scrollbar"
        >
          <div className="flex gap-2 py-3 min-w-max">
            {visibleCats.map((c) => {
              const isActive = activeCat === c.key;
              return (
                <button
                  key={c.key}
                  data-tab={c.key}
                  data-testid={`menu-tab-${c.key}`}
                  onClick={() => scrollTo(c.key)}
                  className={`px-5 py-2.5 text-[11px] tracking-luxe uppercase border transition whitespace-nowrap ${
                    isActive
                      ? "border-ink bg-ink text-white"
                      : "border-black/15 text-black/65 hover:border-ink hover:text-ink"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 space-y-20">
        {CATEGORIES.map((cat) => {
          const list = items.filter((i) => i.category === cat.key);
          if (list.length === 0) return null;
          return (
            <section
              key={cat.key}
              data-testid={`menu-section-${cat.key}`}
              data-cat={cat.key}
              ref={(el) => { sectionRefs.current[cat.key] = el; }}
              className="scroll-mt-36"
            >
              <div className="flex items-center gap-6 mb-10">
                <h2 className="font-display text-3xl md:text-4xl text-ink">{cat.label === "Onyx Ultra" ? "Onyx Ultra Experience" : cat.label}</h2>
                <span className="flex-1 h-px bg-black/10" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8">
                {list.map((item) => (
                  <div key={item.id} className="flex items-end justify-between gap-6 pb-5 border-b border-black/10">
                    <div>
                      <h4 className="font-display text-2xl md:text-3xl text-ink mb-1">{item.name}</h4>
                      <p className="text-sm text-muted max-w-md">{item.description}</p>
                      {item.coming_soon && <span className="inline-block mt-2 text-[10px] tracking-luxe uppercase text-gold">Coming Soon</span>}
                    </div>
                    <div className="font-display text-2xl text-gold whitespace-nowrap">${item.price.toFixed(0)}</div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Tax & Service Fee Notice */}
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 mt-24" data-testid="menu-tax-notice">
        <div className="border-t-2 border-black/10 pt-10">
          <p className="eyebrow mb-4">— Pricing Notice</p>
          <p className="text-sm md:text-base leading-relaxed max-w-3xl" style={{ color: "#1F1F1F" }}>
            All menu prices are subject to the following <span className="text-gold font-medium">10% District of Columbia Liquor Tax</span> &amp; a <span className="text-gold font-medium">20% Service Fee</span>.
          </p>
          <p className="text-sm leading-relaxed max-w-3xl mt-4" style={{ color: "#4A4A4A" }}>
            The 20% Service Fee is assessed to help offset increasing operational expenses and support venue operations. This fee is not a tip or gratuity. Tipping is optional but always appreciated.
          </p>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </main>
  );
}
